// SH-3: Server-issued privilege elevation.
//
// Browser state NEVER creates authority. sessionStorage is used only to decide
// whether the UI should prompt again; the backend independently verifies the
// server-issued elevation grant for every privileged command.

import { supabase } from "@/integrations/supabase/client";

export const STEPUP_TTL_MS = 5 * 60 * 1000; // policy-owned server TTL mirror (UX only)
const KEY = "moveload.stepup.freshAt";

/** Action classes recognised by the server elevation policy. */
export type ActionClass =
  | "SECURITY_ADMIN"
  | "PAYOUT_RELEASE"
  | "BANK_CHANGE"
  | "ROLE_ADMIN"
  | "IMPERSONATE"
  | "KILL_SWITCH"
  | "VERIFICATION_OVERRIDE"
  | "ASSIGNMENT_OVERRIDE"
  | "EVIDENCE_REVEAL"
  | "LAUNCH_TRANSITION"
  | "MASS_EXPORT";

export interface ElevationGrant {
  grantId: string;
  actionClass: ActionClass;
  expiresAt: number;
}

// In-memory only: grants die with the tab and are never persisted.
const grants = new Map<ActionClass, ElevationGrant>();

export function markStepUpFresh() {
  sessionStorage.setItem(KEY, String(Date.now()));
}

export function clearStepUp() {
  sessionStorage.removeItem(KEY);
  grants.clear();
}

export function stepUpFreshMsLeft(): number {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return 0;
  const at = Number(raw);
  if (!Number.isFinite(at)) return 0;
  const left = STEPUP_TTL_MS - (Date.now() - at);
  return left > 0 ? left : 0;
}

/**
 * UX-only hint. Never treat this as authority — the server re-checks the grant.
 */
export function isStepUpFresh(): boolean {
  return stepUpFreshMsLeft() > 0;
}

/** Ask the server for a short-lived elevation grant after proving assurance. */
export async function issueElevation(
  actionClass: ActionClass,
  reason?: string,
  resourceScope: Record<string, unknown> = {},
): Promise<ElevationGrant> {
  const { data, error } = await supabase.rpc("issue_privilege_elevation", {
    _action_class: actionClass,
    _reason: reason ?? null,
    _resource_scope: resourceScope as never,
    _device_fingerprint: deviceFingerprint(),
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.grant_id) throw new Error("elevation_not_issued");
  const grant: ElevationGrant = {
    grantId: row.grant_id as string,
    actionClass,
    expiresAt: new Date(row.expires_at as string).getTime(),
  };
  grants.set(actionClass, grant);
  markStepUpFresh();
  return grant;
}

/** Returns a live in-memory grant for the action class, if one is still valid. */
export function getElevation(actionClass: ActionClass): ElevationGrant | null {
  const g = grants.get(actionClass);
  if (!g) return null;
  if (g.expiresAt <= Date.now()) {
    grants.delete(actionClass);
    return null;
  }
  return g;
}

/** Stable per-browser device fingerprint used for device trust binding. */
export function deviceFingerprint(): string {
  const k = "moveload.device.id";
  let v = localStorage.getItem(k);
  if (!v) {
    v = crypto.randomUUID();
    localStorage.setItem(k, v);
  }
  return v;
}
